import React, { useState, useEffect, useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { CheckCircle2, Pencil, Plus } from 'lucide-react';
import RolesSelector from 'components/Shared/Selectors/RolesSelector';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import userShape from 'propTypes/userShape';
import styles from './Roles.module.scss';
import { useDispatch } from 'react-redux';
import { update as updateUser } from 'redux/modules/auth';
import useAuth from 'hooks/useAuth';
import { trackEvent } from 'helpers/Analytics';
import hocStyles from '../../PortfolioAbout/components/hoc/hoc.module.scss';
import Loader from 'components/Loader/Loader';

const { editBtnPlain, loader, tick } = hocStyles;

// Roles shown as chips before "+N more".
const VISIBLE_ROLES = 3;

const Roles = ({ roles, user, isOwner }) => {
  const { lastUpdated, updating, updated } = useAuth();

  const dispatch = useDispatch();
  const [editRoles, setEditRoles] = useState(roles || []);
  const [editing, setEditing] = useState(false);

  // Update editRoles when roles prop changes (e.g., after resume upload)
  useEffect(() => {
    setEditRoles(roles || []);
  }, [roles]);

  const handleEdit = () => {
    if (JSON.stringify(editRoles) !== JSON.stringify(roles)) {
      dispatch(
        updateUser(
          user,
          { featured_roles: editRoles.length > 0 ? editRoles : [] },
          false,
          [],
          '',
          'featured_roles'
        )
      ).then(() => {
        trackEvent('freelancer-updates-roles');
        setEditing(false);
      });
    } else {
      setEditing(false);
    }
  };

  const instanceIsUpdating = useMemo(
    () => lastUpdated === 'featured_roles',
    [lastUpdated]
  );

  const successTick = !updating &&
    instanceIsUpdating &&
    updated &&
    !editing && <CheckCircle2 className={tick} size={18} aria-label="Saved" />;

  const hasRoles = roles && roles.length > 0;

  // Avoid leaving the min-height roles row empty for visitors
  if (!hasRoles && !isOwner) {
    return null;
  }

  return (
    <div id="portfolio-roles" className={styles.roles}>
      <ul className={styles.chips} aria-label="Roles">
        {hasRoles &&
          roles.slice(0, VISIBLE_ROLES).map((role) => (
            <li key={role}>
              {isOwner ? (
                <span className={styles.chip}>{role}</span>
              ) : (
                <Link
                  className={styles.chip}
                  to={`/find/${toSeoUrl(toPlural(role))}`}
                >
                  {role}
                </Link>
              )}
            </li>
          ))}
        {hasRoles && roles.length > VISIBLE_ROLES && (
          <li>
            <OverlayTrigger
              placement="bottom"
              trigger={['click', 'hover', 'focus']}
              rootClose
              overlay={
                <Popover id="other-user-roles" placement="bottom">
                  <span className="font-bold">Other roles: </span>
                  {roles.slice(VISIBLE_ROLES).map((role, index, array) => (
                    <Fragment key={`other-role-${index}`}>
                      {role}
                      {index < array.length - 1 ? ', ' : ''}
                    </Fragment>
                  ))}
                </Popover>
              }
            >
              <button type="button" className={styles.moreChip}>
                +{roles.length - VISIBLE_ROLES} more
              </button>
            </OverlayTrigger>
          </li>
        )}
        {!hasRoles && (
          <li>
            <button
              type="button"
              className={styles.addChip}
              onClick={() => setEditing(true)}
            >
              <Plus size={12} strokeWidth={2.5} aria-hidden="true" />
              Add your roles
            </button>
          </li>
        )}
      </ul>
      {hasRoles && user && isOwner && (
        <span className={styles.editWrp}>
          {successTick}
          {instanceIsUpdating && updating ? (
            <Loader loading className={loader} />
          ) : (
            <button
              id="edit-roles"
              className={editBtnPlain}
              onClick={() => setEditing(true)}
              type="button"
              aria-label="Edit roles"
            >
              <Pencil size={14} strokeWidth={2} aria-hidden="true" />
            </button>
          )}
        </span>
      )}
      <Modal show={editing && isOwner} onHide={handleEdit}>
        <ModalHeader closeButton>
          <ModalTitle className="font-bold" style={{ marginTop: '2.5px' }}>
            Select your roles
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div>
            Select up to 10 roles from the dropdown below. You can also type to
            search for your matching roles.
          </div>
          <RolesSelector
            existing={editRoles || []}
            onSelect={(selected) =>
              setEditRoles(selected.map(({ value }) => value))
            }
            maxValues={10}
            styles={{
              control: (base) => ({ ...base, minWidth: '150px' }),
            }}
            className="mt+"
          />
        </ModalBody>
        <ModalFooter>
          <Button
            disabled={updating}
            onClick={() => {
              setEditing(false);
              setEditRoles(roles);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={updating}
            onClick={handleEdit}
            bsStyle="primary"
            className="ml--"
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

Roles.propTypes = {
  roles: PropTypes.array,
  editMode: PropTypes.bool,
  updateData: PropTypes.func,
  isOwner: PropTypes.bool,
  toggleEditMode: PropTypes.func,
  user: userShape,
};

export default Roles;
